import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18n from 'i18next';
import { uniq } from 'ramda';
import React from 'react';
import { Trans } from 'react-i18next';

import { getCompanyRoute } from '../../../utils/helpers/routesBuilder';
import useWindowSize from '../../../utils/hooks/useWindowSize';
import { DisplayFlexS } from '../../../utils/stylesHelpers';
import CompanyBestOfferDeImg from '../../assets/imgs/companyBestesAngebot_de.svg';
import CompanyBestOfferEnImg from '../../assets/imgs/companyBestOffer_en.svg';
import CompanySpecialOfferDeImg from '../../assets/imgs/companySpecialOffer.svg';
import CompanySpecialOfferEnImg from '../../assets/imgs/companySpecialOffer_en.svg';
import GlobeIcon from '../../assets/imgs/icons/globeIcon.svg';
import LocationIcon from '../../assets/imgs/icons/Location_Icon.svg';
import MailIcon from '../../assets/imgs/icons/mailIcon.svg';
import NoLogo from '../../assets/imgs/icons/NOlogo_Icon.svg';
import PhoneIcon from '../../assets/imgs/icons/phone_icon.svg';
import ProfileClicksIcon from '../../assets/imgs/icons/Profile_Clicks_Icon.svg';
import VerifiedIcon from '../../assets/imgs/icons/Verified_Icon.svg';
import { COMMUNICATION_PREF_TYPE, COMMUNICATION_PREF_ICON } from '../../constants/company';
import { SCREEN_SIZES } from '../../constants/screenSizes';
import { Text, PrimaryButtonS } from '../common';
import Icon from '../common/Icon';
import { TextCHF } from '../company/companyItemS';
import { CompanyRatingCombined } from '../company/companyRating';

import {
  CompanyCardWrapS,
  CompanyLogoWrapS,
  CompanyLogoImageWrap,
  CompanyHeaderWrapS,
  CompanyHeaderS,
  CompanySpecialOfferImageWrap,
  ContentCompanyWrapper,
  InfoWrapperS,
  InfoTitleItemHeaderS,
  InfoBodyS,
  InfoBodyItemS,
  TagInfoWrapper,
  MobileCompanyCardWrapS,
  CompanyMobileHeaderS,
  CompanyLogoS,
  CompanyNameS,
  VerifiedIconS,
  RatingSectionS,
  ContactInfoS,
  ContactItemS,
  ContactIconS,
  ContactTextS,
  ServicesSectionS,
  ServiceTitleS,
  ServiceListS,
  ServiceItemS,
  ViewsSectionS,
  InfoSectionS,
} from './companyCardS';

const InfoItem = ({ children, round, backgroundColor }) => (
  <InfoBodyItemS round={round} backgroundColor={backgroundColor}>
    {children}
  </InfoBodyItemS>
);

const InfoSection = ({ title, items }) =>
  items &&
  items.length > 0 && (
    <ServicesSectionS>
      <ServiceTitleS>{title}</ServiceTitleS>
      <ServiceListS>
        {items.map((item, index) => (
          <ServiceItemS key={index}>{item}</ServiceItemS>
        ))}
      </ServiceListS>
    </ServicesSectionS>
  );

  const OFFER_IMAGES = { 
    de: {
      speciallOffer: CompanySpecialOfferDeImg,
      bestOffer: CompanyBestOfferDeImg
    },
    en: {
      speciallOffer: CompanySpecialOfferEnImg, 
      bestOffer: CompanyBestOfferEnImg
    }
  };

const CompanyCard = ({
  _id,
  logo,
  name,
  verified,
  base,
  communicationPreferences,
  services,
  rate,
  software,
  planTier,
  views,
  rating,
  ratingFromOtherSources,
  ratingFromOthersSum,
  push,
  languages,
  member,
}) => {
  const { width: windowWidth } = useWindowSize();
  const uniqueSpecialisations = uniq(services ? services.specialisation : []);
  const uniqueSoftware = uniq(software || []);
  const currentLanguage = i18n.language;

  const hasServices = (services && services.basics) || uniqueSpecialisations.length > 0;
  const hasRate = rate && Object.keys(rate).length > 0;
  const hasSoftware = uniqueSoftware.length > 0;

  return windowWidth > SCREEN_SIZES.MEDIUM ? (
    <CompanyCardWrapS>
      <CompanyLogoWrapS isLogo={logo}>
        <CompanyLogoImageWrap logo={logo || NoLogo} bz={logo ? 'contain' : '50%'} />
      </CompanyLogoWrapS>
      <DisplayFlexS direction="column" directionScreenMedium="columns" style={{ width: '100%' }}>
        <CompanyHeaderWrapS>
          <CompanyHeaderS>
            <CompanyLogoWrapS mobile>
              <CompanyLogoImageWrap logo={logo || NoLogo} bz={logo ? 'contain' : '50%'} />
            </CompanyLogoWrapS>
            <Text style={{ marginRight: '10px' }} color="violet" size="largeText">
              {name}
            </Text>
            {verified && <Icon src={VerifiedIcon} alt="verified-icon" />}

            {planTier &&
              <CompanySpecialOfferImageWrap 
                src={ planTier==="premium" 
                  ? OFFER_IMAGES[currentLanguage].speciallOffer : OFFER_IMAGES[currentLanguage].bestOffer} 
              />
            }

          </CompanyHeaderS>
          <CompanyRatingCombined
            internalRating={rating}
            onInternalReviewsCountClick={(e) => {
              e.preventDefault();
              push(getCompanyRoute(_id, name), { scrollToReviews: true });
            }}
            ratingViewCompany
            {...{ ratingFromOtherSources, ratingFromOthersSum }}
          />
        </CompanyHeaderWrapS>
        <ContentCompanyWrapper>
          <DisplayFlexS justify="space-between" directionScreenMedium="column" style={{ height: '100%' }}>
            <DisplayFlexS direction="column" directionScreenMedium="column" style={{ width: 'auto', height: '100%' }}>
              {hasServices && (
                <InfoWrapperS>
                  <InfoTitleItemHeaderS>
                    <Trans i18nKey="companies.companyCard.servicesLabel" />:
                  </InfoTitleItemHeaderS>
                  <InfoBodyS>
                    {services && services.basics && (
                      <InfoItem round backgroundColor="grayBlue">
                        <Trans i18nKey="companies.companyCard.basicService" />
                      </InfoItem>
                    )}
                    {uniqueSpecialisations.map((item) => (
                      <InfoItem key={item}>{item}</InfoItem>
                    ))}
                  </InfoBodyS>
                </InfoWrapperS>
              )}
              {hasRate && (
                <InfoWrapperS>
                  <InfoTitleItemHeaderS>
                    <Trans i18nKey="companies.companyCard.rateLabel" />:
                  </InfoTitleItemHeaderS>
                  <InfoBodyS>
                    {Object.keys(rate).map(
                      (rateSize) =>
                        rate[rateSize] && (
                          <InfoItem key={rateSize}>
                            <Text color="violet" style={{ marginBottom: 10 }}>
                              {i18n.t([`companies.companyCard.rates.${rateSize}`, [rateSize]])}
                            </Text>
                            <TextCHF>{`CHF ${rate[rateSize]}`}</TextCHF>
                          </InfoItem>
                        )
                    )}
                  </InfoBodyS>
                </InfoWrapperS>
              )}
              {hasSoftware && (
                <InfoWrapperS>
                  <InfoTitleItemHeaderS>
                    <Trans i18nKey="companies.companyCard.softwareLabel" />:
                  </InfoTitleItemHeaderS>
                  <InfoBodyS>
                    {uniqueSoftware.slice(0, 4).map((item) => (
                      <InfoItem key={item}>{item}</InfoItem>
                    ))}
                    {uniqueSoftware.length > 4 && (
                      <InfoItem round backgroundColor="beige">
                        {`+ ${uniqueSoftware.slice(4).length}`}
                      </InfoItem>
                    )}
                  </InfoBodyS>
                </InfoWrapperS>
              )}
            </DisplayFlexS>
            <DisplayFlexS
              direction="column"
              justify="space-between"
              directionScreenMedium="column"
              style={{ width: '165px', height: '100%' }}
            >
              <DisplayFlexS direction="column" directionScreenMedium="column" style={{ marginBottom: '35px' }}>
                {base && base.address && base.address.city && (
                  <TagInfoWrapper>
                    <Icon src={LocationIcon} alt="location-icon" />
                    <Text color="darkerViolet" size="bigText" align="left">
                      {base.address.city}
                    </Text>
                  </TagInfoWrapper>
                )}
                {communicationPreferences && (
                  <TagInfoWrapper>
                    <FontAwesomeIcon
                      icon={
                        communicationPreferences === COMMUNICATION_PREF_TYPE.ONLINE
                          ? COMMUNICATION_PREF_ICON.ONLINE
                          : COMMUNICATION_PREF_ICON.FACE_TO_FACE
                      }
                    />
                    <Text color="darkerViolet" size="bigText" align="left">
                      <Trans i18nKey={'companies.companyCard.communicationPreferences.' + communicationPreferences} />
                    </Text>
                  </TagInfoWrapper>
                )}
                <TagInfoWrapper>
                  <Icon src={ProfileClicksIcon} alt="profile-clicks-icon" />
                  <Text color="darkerViolet" size="bigText" align="left">
                    {views} <Trans i18nKey="companies.companyCard.viewsLabel" />
                  </Text>
                </TagInfoWrapper>
              </DisplayFlexS>
              <PrimaryButtonS padding="7px 26px" fz="regular" style={{ width: '100%' }}>
                <Text color="white">
                  <Trans i18nKey="companies.companyCard.viewProfileBtn" />
                </Text>
              </PrimaryButtonS>
            </DisplayFlexS>
          </DisplayFlexS>
        </ContentCompanyWrapper>
      </DisplayFlexS>
    </CompanyCardWrapS>
  ) : (
    <MobileCompanyCardWrapS>
      <CompanyMobileHeaderS>
        <CompanyLogoS src={logo || 'https://via.placeholder.com/50'} alt={`${name} logo`} />
        <div>
          <CompanyNameS>
            <h4>{name}</h4>
            {verified && <VerifiedIconS src={VerifiedIcon} alt="Verified" />}
          </CompanyNameS>
          <RatingSectionS>
            <CompanyRatingCombined
              internalRating={rating}
              onInternalReviewsCountClick={(e) => {
                e.preventDefault();
                push(getCompanyRoute(_id, name), { scrollToReviews: true });
              }}
              ratingViewCompany
              {...{ ratingFromOtherSources, ratingFromOthersSum }}
            />
          </RatingSectionS>
        </div>
      </CompanyMobileHeaderS>
      <ContactInfoS>
        {base && base.address && (
          <>
            <ContactItemS>
              <ContactIconS src={LocationIcon} />
              <ContactTextS>{`${base.address.street}, ${base.address.zip}, ${base.address.city}`}</ContactTextS>
            </ContactItemS>
            <ContactItemS>
              <ContactIconS src={PhoneIcon} />
              <ContactTextS>{base.tel}</ContactTextS>
            </ContactItemS>
            <ContactItemS>
              <ContactIconS src={MailIcon} />
              <ContactTextS>{base.email}</ContactTextS>
            </ContactItemS>
            <ContactItemS>
              <ContactIconS src={GlobeIcon} />
              <ContactTextS>{base.website}</ContactTextS>
            </ContactItemS>
          </>
        )}
      </ContactInfoS>
      <InfoSectionS>
        <InfoSection
          title={i18n.t('companies.companyCard.infoTitles.services')}
          items={services && services.specialisation}
        />
        <InfoSection title={i18n.t('companies.companyCard.infoTitles.languages')} items={languages} />
        <InfoSection title={i18n.t('companies.companyCard.infoTitles.memberships')} items={member} />
      </InfoSectionS>

      <ViewsSectionS>
        {views} {i18n.t('companies.companyCard.infoTitles.views')}
      </ViewsSectionS>
    </MobileCompanyCardWrapS>
  );
};

export default CompanyCard;
